import React, { useState, useEffect } from 'react';
import DataTable from './DataTable';
import axios from 'axios';
import Loader from './Loader';

const Discoms = () => {
    const [discoms, setDiscoms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingDiscom, setEditingDiscom] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [formData, setFormData] = useState({
        discomName: '',
        zone: '',
        circle: '',
        division: ''
    });

    useEffect(() => {
        fetchDiscoms();
    }, []);

    const fetchDiscoms = async () => {
        try {
            console.log('Fetching discoms...');
            const response = await axios.get('/api/discoms');
            console.log('Discoms response:', response.data);
            setDiscoms(response.data);
        } catch (error) {
            console.error('Error fetching discoms:', error);
            alert('Error fetching discoms: ' + (error.response?.data || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingDiscom) {
                await axios.put(`/api/discoms/${editingDiscom.id}`, {
                    ...formData,
                    id: editingDiscom.id
                });
            } else {
                await axios.post('/api/discoms', formData);
            }
            fetchDiscoms();
            resetForm();
        } catch (error) {
            console.error('Error saving discom:', error);
            if (error.response?.status === 409) {
                alert(error.response.data.message || 'Discom with same combination already exists');
            } else {
                alert('Error saving discom');
            }
        }
    };

    const handleEdit = (discom) => {
        setEditingDiscom(discom);
        setFormData({
            discomName: discom.discomName,
            zone: discom.zone,
            circle: discom.circle,
            division: discom.division
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this discom?')) {
            try {
                await axios.delete(`/api/discoms/${id}`);
                fetchDiscoms();
            } catch (error) {
                console.error('Error deleting discom:', error);
                alert('Error deleting discom');
            }
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            alert('Please select a file');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);

        try {
            const response = await axios.post('/api/discoms/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            alert(`Successfully imported ${response.data.count} records`);
            fetchDiscoms();
            setShowUploadModal(false);
            setUploadFile(null);
        } catch (error) {
            console.error('Error uploading file:', error);
            alert(error.response?.data || 'Error uploading file');
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            discomName: '',
            zone: '',
            circle: '',
            division: ''
        });
        setEditingDiscom(null);
        setShowModal(false);
    };

    const columns = [
        { key: 'discomName', field: 'discomName', label: 'Discom Name', sortable: true },
        { key: 'zone', field: 'zone', label: 'Zone', sortable: true },
        { key: 'circle', field: 'circle', label: 'Circle', sortable: true },
        { key: 'division', field: 'division', label: 'Division', sortable: true }
    ];

    if (loading) {
        return (
            <div className="table-loader">
                <Loader text="Loading discoms..." size="medium" />
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="fas fa-building me-2"></i>
                                Discom Management
                            </h5>
                            <div className="page-header-actions">
                                <button 
                                    className="btn btn-success me-2" 
                                    onClick={() => setShowUploadModal(true)}
                                >
                                    <i className="fas fa-upload me-1"></i>
                                    Upload File
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={() => setShowModal(true)}
                                >
                                    <i className="fas fa-plus me-1"></i>
                                    Add Discom
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="mb-2">
                                <small className="text-muted">Total records: {discoms.length}</small>
                            </div>
                            <DataTable
                                data={discoms}
                                columns={columns}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                itemsPerPage={25}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingDiscom ? 'Edit Discom' : 'Add New Discom'}
                                </h5>
                                <button type="button" className="btn-close" onClick={resetForm}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Discom Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.discomName}
                                            onChange={(e) => setFormData({...formData, discomName: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Zone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.zone}
                                            onChange={(e) => setFormData({...formData, zone: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Circle</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.circle}
                                            onChange={(e) => setFormData({...formData, circle: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Division</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.division}
                                            onChange={(e) => setFormData({...formData, division: e.target.value})}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingDiscom ? 'Update' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Upload Discom Data</h5>
                                <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
                            </div>
                            <form onSubmit={handleFileUpload}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Select CSV File</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept=".csv"
                                            onChange={(e) => setUploadFile(e.target.files[0])}
                                            required
                                        />
                                        <div className="form-text">
                                            CSV file should have columns: S.N., Discom, Zone, Circle, Division
                                        </div>
                                    </div>
                                    <div className="alert alert-info">
                                        <strong>CSV File Format:</strong><br/>
                                        Column 1: S.N. (Serial Number - will be ignored)<br/>
                                        Column 2: Discom<br/>
                                        Column 3: Zone<br/>
                                        Column 4: Circle<br/>
                                        Column 5: Division<br/><br/>
                                        <strong>Example:</strong><br/>
                                        S.N.,Discom,Zone,Circle,Division<br/>
                                        1,PVVNL,GHAZIABAD II,EDC Loni,EDD II LONI
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-success" disabled={uploading}>
                                        {uploading ? 'Uploading...' : 'Upload'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Discoms;
